<template>
  <div id="app">
    <component :is="layout" v-if="layout" />
  </div>
</template>

<style>
/* When browser zoom exceeds 300%, drop the fixed/sticky header so users
   on extreme zoom levels recover viewport space. */
body.zoom-no-sticky-header .mdk-header-layout > .mdk-header,
body.zoom-no-sticky-header .mdk-header-layout[data-fullbleed] > .mdk-header,
body.zoom-no-sticky-header .mdk-header-layout[data-has-scrolling-region] > .mdk-header {
  position: static !important;
}
body.zoom-no-sticky-header .mdk-header-layout {
  overflow: visible !important;
}
</style>

<script>
const { name, version } = require('~/package.json');
import { Session } from 'fm-app-settings';
import { mapGetters, mapActions } from 'vuex';
import { get, isEmpty } from 'lodash';
import { DEFAULT_FAVICON, USER_ROLE_TYPES, DASHBOARD_TYPES, ENTITY_TYPES } from './common/constants';
import App from './layouts/app.vue';
import Sticky from './layouts/sticky.vue';
import Boxed from './layouts/boxed.vue';
import Fixed from './layouts/fixed.vue';
import Blank from './layouts/blank.vue';

export default {
  head() {
    return {
      title: this.$store.state && this.$store.state.brand,
      htmlAttrs: {
        dir: `${this.$store.state && this.$store.getters.settings.layout.rtl ? 'rtl' : 'ltr'}`,
      },
    };
  },
  components: {
    App,
    Sticky,
    Boxed,
    Blank,
    Fixed,
  },
  data() {
    return {
      session: null,
      zoomCheckHandler: null,
    };
  },
  computed: {
    ...mapGetters('auth', ['getLoggedInUser', 'getLoggedInUserDashboard']),
    layout() {
      return this.$store.getters.settings.layout.layout;
    },
  },
  methods: {
    ...mapActions('school', ['getSchoolFromCurrentDomain']),
    ...mapActions('navbarConfig', ['updateNavbarBrandDetails']),
    ...mapActions('student', ['getPiiStatus']),
    applyZoomHeaderState() {
      // Estimate browser zoom %. outerWidth/innerWidth is reliable in
      // Chromium browsers; fall back to devicePixelRatio for others.
      let zoom = 100;
      try {
        if (window.outerWidth && window.innerWidth) {
          zoom = Math.round((window.outerWidth / window.innerWidth) * 100);
        }
        // Sanity fallback (e.g. Safari sometimes returns same outer/inner).
        if (zoom <= 100 && window.devicePixelRatio) {
          const dprZoom = Math.round(window.devicePixelRatio * 100);
          if (dprZoom > zoom) zoom = dprZoom;
        }
      } catch (_e) {
        zoom = 100;
      }
      const shouldUnstick = zoom > 300;
      document.body.classList.toggle('zoom-no-sticky-header', shouldUnstick);
    },
  },
  mounted() {
    this.applyZoomHeaderState();
    this.zoomCheckHandler = () => this.applyZoomHeaderState();
    window.addEventListener('resize', this.zoomCheckHandler);
  },
  beforeDestroy() {
    if (this.zoomCheckHandler) {
      window.removeEventListener('resize', this.zoomCheckHandler);
    }
  },
  async beforeMount() {
    this.session = new Session(`${name}-${version}`);

    if (
      get(this.getLoggedInUser, 'role_type') === USER_ROLE_TYPES.STUDENT &&
      this.getLoggedInUserDashboard === DASHBOARD_TYPES.SCHOOL
    ) {
      // Get student PII status on every refresh.
      this.getPiiStatus();
    }

    if (
      (get(this.getLoggedInUser, 'role_type') === USER_ROLE_TYPES.SCHOOL ||
        get(this.getLoggedInUser, 'role_type') === USER_ROLE_TYPES.STUDENT ||
        get(this.getLoggedInUser, 'role_type') === USER_ROLE_TYPES.ORGANIZATION) &&
      this.getLoggedInUser.linked_entity
    ) {
      document.documentElement.style.setProperty('--primary-color', this.getLoggedInUser.linked_entity.primary_color);
      document.documentElement.style.setProperty(
        '--secondary-color',
        this.getLoggedInUser.linked_entity.secondary_color,
      );
      document.documentElement.style.setProperty('--dark-color', this.getLoggedInUser.linked_entity.dark_color);
      const favicon = document.getElementById('favicon');
      favicon.href = get(this.getLoggedInUser, 'linked_entity.small_logo_url') || DEFAULT_FAVICON;
    }

    if (
      get(this.getLoggedInUser, 'role_type') === USER_ROLE_TYPES.EMPLOYER &&
      get(this.getLoggedInUser.linked_entity, 'organization')
    ) {
      document.documentElement.style.setProperty(
        '--primary-color',
        get(this.getLoggedInUser.linked_entity, 'organization.primary_color'),
      );
      document.documentElement.style.setProperty(
        '--secondary-color',
        get(this.getLoggedInUser.linked_entity, 'organization.secondary_color'),
      );
      document.documentElement.style.setProperty(
        '--dark-color',
        get(this.getLoggedInUser.linked_entity, 'organization.dark_color'),
      );
    }

    //refresh check
    try {
      const school = await this.getSchoolFromCurrentDomain({ storeSchool: true });
      if (!isEmpty(school)) {
        document.documentElement.style.setProperty('--primary-color', school.primary_color);
        document.documentElement.style.setProperty('--secondary-color', school.secondary_color);
        document.documentElement.style.setProperty('--dark-color', school.dark_color);
        const favicon = document.getElementById('favicon');
        favicon.href = get(school, 'small_logo_url') || DEFAULT_FAVICON;
        this.updateNavbarBrandDetails(school);
      }
    } catch (_err) {
      // Handle error if needed.
    }

    // Org Career Center + CareCampus theming. The org-branded Career Center
    // (ORGANIZATION) uses the organization's brand colors. TransitionU
    // (CARECAMPUS, school-backed) uses the Transition brand, unless the
    // CareCampus is itself org-backed (then the org's colors win).
    const linkedEntity = this.getLoggedInUser.linked_entity;
    const setBrand = (p, s, d) => {
      document.documentElement.style.setProperty('--primary-color', p);
      document.documentElement.style.setProperty('--secondary-color', s);
      document.documentElement.style.setProperty('--dark-color', d);
    };
    if (this.getLoggedInUserDashboard === DASHBOARD_TYPES.ORGANIZATION && get(linkedEntity, 'primary_color')) {
      setBrand(linkedEntity.primary_color, linkedEntity.secondary_color, linkedEntity.dark_color);
    } else if (this.getLoggedInUserDashboard === DASHBOARD_TYPES.CARECAMPUS) {
      if (get(linkedEntity, 'entity_type') === ENTITY_TYPES.ORGANIZATION && get(linkedEntity, 'primary_color')) {
        setBrand(linkedEntity.primary_color, linkedEntity.secondary_color, linkedEntity.dark_color);
      } else {
        setBrand('#3B86F5', '#2F6FED', '#181818');
      }
    }
  },
};
</script>
